/**
 * @fileoverview 키링 커팅 라인 생성에 필요한 유틸리티 클래스들을 제공합니다.
 * @module NessLabs/F/Function/composite.utils
 */

import { loadImageFromUrl } from '../../../Canvas/S/util.js';
import { UtilS } from '../../../Util/S/Function/module/UtilS.js';
import { SVG_DEFAULTS } from '../../S/Constant/composite.js';
import { CanvasUtils } from '../../S/Function/canvas.utils.js';

/**
 * 파일 다운로드 관련 기능을 제공하는 유틸리티 클래스
 * @class FileDownloader
 */
export class FileDownloader {
  /**
   * SVG 엘리먼트를 파일로 다운로드합니다.
   * @static
   * @param {SVGElement} svgElement - 다운로드할 SVG 엘리먼트
   * @param {string} filename - 저장될 파일명
   * @param {number} [dpi=72] - 이미지의 DPI 값
   * @returns {void}
   */
  static downloadSvg(svgElement, filename, dpi = 72) {
    const svgWithDpi = SvgUtils.injectDpi(svgElement, dpi);
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgWithDpi);
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    this.#triggerSecureDownload(svgBlob, filename);
  }

  /**
   * Canvas 엘리먼트를 PNG 파일로 다운로드합니다.
   * @static
   * @param {HTMLCanvasElement} canvas - 다운로드할 Canvas 엘리먼트
   * @param {string} [filename='canvas-image.png'] - 저장될 파일명
   * @param {number} [dpi=300] - 이미지의 DPI 값
   * @returns {void}
   */
  static downloadCanvas(canvas, filename = 'canvas-image.png', dpi = 72) {
    canvas.toBlob((blob) => {
      // DPI 메타데이터를 포함한 새로운 Blob 생성
      const reader = new FileReader();
      reader.onload = () => {
        const arrayBuffer = reader.result;
        const view = new DataView(arrayBuffer);
        const newArrayBuffer = new ArrayBuffer(arrayBuffer.byteLength + 21); // pHYs chunk size
        const newView = new DataView(newArrayBuffer);

        // PNG 시그니처와 IHDR 청크 복사
        for (let i = 0; i < 33; i++) {
          newView.setUint8(i, view.getUint8(i));
        }

        // pHYs 청크 추가
        const pixelsPerMeter = Math.round(dpi * 39.3701); // DPI to pixels per meter

        newView.setUint32(33, 9); // pHYs chunk length
        newView.setUint32(37, 0x70485973); // "pHYs"
        newView.setUint32(41, pixelsPerMeter); // pixels per meter X
        newView.setUint32(45, pixelsPerMeter); // pixels per meter Y
        newView.setUint8(49, 1); // unit is meters

        // CRC32 계산
        const crc = this.#calculateCRC32(new Uint8Array(newArrayBuffer.slice(37, 50)));
        newView.setUint32(50, crc);

        // 나머지 데이터 복사
        for (let i = 33; i < arrayBuffer.byteLength; i++) {
          newView.setUint8(i + 21, view.getUint8(i));
        }

        const newBlob = new Blob([newArrayBuffer], { type: 'image/png' });
        this.#triggerSecureDownload(newBlob, filename);
      };
      reader.readAsArrayBuffer(blob);
    }, 'image/png');
  }

  /**
   * 안전한 파일 다운로드를 실행합니다.
   * @static
   * @private
   * @param {Blob} blob - 다운로드할 Blob 데이터
   * @param {string} filename - 저장될 파일명
   * @returns {void}
   */
  static #triggerSecureDownload(blob, filename) {
    try {
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.style.display = 'none';
      link.href = blobUrl;
      link.download = filename;
      link.rel = 'noopener';

      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      // Blob URL을 즉시 해제하지 않고 약간의 지연을 줌
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (error) {
      console.error('Failed to download file:', error);
    }
  }

  /**
   * CRC32 체크섬을 계산합니다.
   * @static
   * @private
   * @param {Uint8Array} data - CRC32를 계산할 데이터
   * @returns {number} 계산된 CRC32 값
   */
  static #calculateCRC32(data) {
    let crc = -1;
    const table = new Int32Array(256);

    for (let i = 0; i < 256; i++) {
      let c = i;
      for (let j = 0; j < 8; j++) {
        c = c & 1 ? -306674912 ^ (c >>> 1) : c >>> 1;
      }
      table[i] = c;
    }

    for (let i = 0; i < data.length; i++) {
      crc = table[(crc ^ data[i]) & 0xff] ^ (crc >>> 8);
    }

    return ~crc;
  }
}

/**
 * SVG 관련 유틸리티 기능을 제공하는 클래스
 * @class SvgUtils
 */
export class SvgUtils {
  /**
   * SVG에 DPI 정보를 주입합니다.
   * @static
   * @param {SVGElement} svgElement - DPI를 주입할 SVG 엘리먼트
   * @param {number} [dpi=72] - 이미지의 DPI 값
   * @returns {SVGElement} DPI가 주입된 SVG 엘리먼트의 복제본
   */
  static injectDpi(svgElement, dpi = 72) {
    const svgClone = svgElement.cloneNode(true);

    // SVG에 DPI 정보 설정
    const pixelsPerInch = dpi;
    const pixelsPerCm = pixelsPerInch / 2.54;

    svgClone.setAttribute('width', `${svgClone.width.baseVal.value / pixelsPerCm}cm`);
    svgClone.setAttribute('height', `${svgClone.height.baseVal.value / pixelsPerCm}cm`);

    // viewBox가 없는 경우 추가
    if (
      !svgClone.hasAttribute('viewBox') &&
      svgClone.hasAttribute('width') &&
      svgClone.hasAttribute('height')
    ) {
      svgClone.setAttribute(
        'viewBox',
        `0 0 ${svgClone.width.baseVal.value} ${svgClone.height.baseVal.value}`,
      );
    }

    return svgClone;
  }

  /**
   * 커팅 라인 SVG를 생성합니다.
   * @static
   * @param {Object} params - SVG 생성 파라미터
   * @param {string} params.pathData - SVG 패스 데이터
   * @param {string} [params.color=SVG_DEFAULTS.COLOR] - 선 색상
   * @param {number} [params.strokeWidth=SVG_DEFAULTS.STROKE_WIDTH] - 선 두께
   * @param {number} [params.width] - SVG 너비 (지정하지 않으면 자동 계산)
   * @param {number} [params.height] - SVG 높이 (지정하지 않으면 자동 계산)
   * @returns {SVGElement} 생성된 SVG 엘리먼트
   */
  static createCutlineSvg({
    pathData,
    color = SVG_DEFAULTS.COLOR,
    strokeWidth = SVG_DEFAULTS.STROKE_WIDTH,
    width,
    height,
  }) {
    const svg = document.createElementNS(SVG_DEFAULTS.SVG_NAMESPACE, 'svg');

    if (width && height) {
      this.setSvgDimensions(svg, width, height);
    } else {
      this.setAutoSvgDimensions(svg, pathData, strokeWidth);
    }

    const path = this.createSvgPath(pathData, color, strokeWidth);
    svg.appendChild(path);
    return svg;
  }

  /**
   * SVG의 크기를 설정합니다.
   * @static
   * @private
   * @param {SVGElement} svg - SVG 엘리먼트
   * @param {number} width - 너비
   * @param {number} height - 높이
   * @returns {void}
   */
  static setSvgDimensions(svg, width, height) {
    svg.setAttribute('width', width);
    svg.setAttribute('height', height);
    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  }

  /**
   * 패스 데이터를 기반으로 SVG 크기를 자동 설정합니다.
   * @static
   * @private
   * @param {SVGElement} svg - SVG 엘리먼트
   * @param {string} pathData - SVG 패스 데이터
   * @param {number} strokeWidth - 선 두께
   * @returns {void}
   */
  static setAutoSvgDimensions(svg, pathData, strokeWidth) {
    const strokeWidthNum = parseFloat(strokeWidth);
    const strokeMargin = strokeWidthNum / 2;
    const bbox = this.getPathBoundingBox(pathData);

    svg.setAttribute('width', bbox.width + strokeWidthNum);
    svg.setAttribute('height', bbox.height + strokeWidthNum);
    svg.setAttribute(
      'viewBox',
      `${bbox.x - strokeMargin} ${bbox.y - strokeMargin} ${bbox.width + strokeWidthNum} ${
        bbox.height + strokeWidthNum
      }`,
    );
  }

  /**
   * 패스의 경계 상자를 계산합니다.
   * @static
   * @private
   * @param {string} pathData - SVG 패스 데이터
   * @returns {DOMRect} 경계 상자 정보
   */
  static getPathBoundingBox(pathData) {
    const tempSvg = document.createElementNS(SVG_DEFAULTS.SVG_NAMESPACE, 'svg');
    const tempPath = document.createElementNS(SVG_DEFAULTS.SVG_NAMESPACE, 'path');
    tempPath.setAttribute('d', pathData);
    tempSvg.appendChild(tempPath);
    document.body.appendChild(tempSvg);
    const bbox = tempPath.getBBox();
    document.body.removeChild(tempSvg);
    return bbox;
  }

  /**
   * SVG 패스 엘리먼트를 생성합니다.
   * @static
   * @private
   * @param {string} pathData - SVG 패스 데이터
   * @param {string} color - 선 색상
   * @param {number} strokeWidth - 선 두께
   * @returns {SVGPathElement} 생성된 패스 엘리먼트
   */
  static createSvgPath(pathData, color, strokeWidth) {
    const path = document.createElementNS(SVG_DEFAULTS.SVG_NAMESPACE, 'path');
    path.setAttribute('d', pathData);
    path.setAttribute('stroke', color);
    path.setAttribute('stroke-width', strokeWidth);
    path.setAttribute('fill', 'none');
    return path;
  }

  /**
   * SVG 엘리먼트에 이미지를 포함시켜 반환합니다.
   * @static
   * @param {SVGElement} svgElement - 원본 SVG 엘리먼트
   * @param {HTMLCanvasElement} imageCanvas - 포함시킬 이미지 캔버스
   * @returns {SVGElement} 이미지가 포함된 새로운 SVG 엘리먼트
   */
  static addImageToSvg(svgElement, imageCanvas) {
    // 새로운 SVG 엘리먼트 생성
    const newSvg = document.createElementNS(SVG_DEFAULTS.SVG_NAMESPACE, 'svg');

    // 원본 SVG의 속성들을 복사
    Array.from(svgElement.attributes).forEach((attr) => {
      newSvg.setAttribute(attr.name, attr.value);
    });

    // SVG 크기가 이미지 크기와 일치하도록 설정
    newSvg.setAttribute('width', imageCanvas.width);
    newSvg.setAttribute('height', imageCanvas.height);
    newSvg.setAttribute('viewBox', `0 0 ${imageCanvas.width} ${imageCanvas.height}`);

    // 이미지 엘리먼트 생성
    const image = document.createElementNS(SVG_DEFAULTS.SVG_NAMESPACE, 'image');
    image.setAttribute('width', imageCanvas.width);
    image.setAttribute('height', imageCanvas.height);
    image.setAttribute('x', '0');
    image.setAttribute('y', '0');

    // Canvas를 base64 이미지로 변환
    const imageData = imageCanvas.toDataURL('image/png');
    image.setAttributeNS('http://www.w3.org/1999/xlink', 'href', imageData);

    // 이미지를 새 SVG에 추가
    newSvg.appendChild(image);

    // 원본 SVG의 모든 자식 노드들을 복사하여 추가
    Array.from(svgElement.children).forEach((child) => {
      newSvg.appendChild(child.cloneNode(true));
    });

    return newSvg;
  }

  /**
   * SVG 내부의 모든 path 요소들의 최대 경계 영역을 계산하고, 해당 영역에 맞게 SVG를 조정합니다.
   * @static
   * @param {SVGElement | Node} svgElement - 조정할 SVG 엘리먼트
   * @returns {{
   *   svgElement: SVGElement,
   *   bounds: {x: number, y: number, width: number, height: number}
   * }} 조정된 SVG 엘리먼트와 경계 정보
   */
  static fitSvgToPathBounds(svgElement) {
    // 임시 SVG를 생성하여 DOM에 추가 (getBBox 계산을 위해 필요)
    const tempSvg = svgElement.cloneNode(true);
    document.body.appendChild(tempSvg);

    try {
      // 모든 path 요소의 경계를 계산
      const paths = tempSvg.getElementsByTagName('path');
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;

      Array.from(paths).forEach((path) => {
        const bbox = path.getBBox();
        minX = Math.min(minX, bbox.x);
        minY = Math.min(minY, bbox.y);
        maxX = Math.max(maxX, bbox.x + bbox.width);
        maxY = Math.max(maxY, bbox.y + bbox.height);
      });

      // 경계 영역 계산
      const bounds = {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
      };

      // stroke-width를 고려한 여백 추가
      const strokeWidth = parseFloat(paths[0]?.getAttribute('stroke-width') || '1');
      const margin = strokeWidth / 2;

      // SVG 속성 업데이트
      const adjustedBounds = {
        x: bounds.x - margin,
        y: bounds.y - margin,
        width: bounds.width + strokeWidth,
        height: bounds.height + strokeWidth,
      };

      const newSvg = svgElement.cloneNode(true);
      newSvg.setAttribute('width', adjustedBounds.width);
      newSvg.setAttribute('height', adjustedBounds.height);
      newSvg.setAttribute(
        'viewBox',
        `${adjustedBounds.x} ${adjustedBounds.y} ${adjustedBounds.width} ${adjustedBounds.height}`,
      );

      return { svgElement: newSvg, bounds: adjustedBounds };
    } finally {
      // 임시 SVG 제거
      document.body.removeChild(tempSvg);
    }
  }

  /**
   * SVG 엘리먼트의 크기를 주어진 scale 값에 따라 조정합니다.
   * @static
   * @param {Object} params - 스케일링 파라미터
   * @param {SVGElement} params.svgElement - 크기를 조정할 SVG 엘리먼트
   * @param {number} params.scale - 적용할 스케일 값 (예: 1.5는 50% 증가)
   * @returns {SVGElement} 크기가 조정된 새로운 SVG 엘리먼트
   */
  static scaleSvg({ svgElement, scale }) {
    const newSvg = svgElement.cloneNode(false); // shallow clone

    // width와 height 스케일 적용
    const width = svgElement.width.baseVal.value * scale;
    const height = svgElement.height.baseVal.value * scale;

    // viewBox는 그대로 유지 (스케일링하지 않음)
    const viewBox = svgElement.getAttribute('viewBox');

    // 새로운 SVG 속성 설정
    newSvg.setAttribute('width', width);
    newSvg.setAttribute('height', height);
    if (viewBox) {
      newSvg.setAttribute('viewBox', viewBox);
    }

    // 모든 자식 요소들을 복사하고 transform 적용
    Array.from(svgElement.children).forEach((child) => {
      const newChild = child.cloneNode(true);

      const existingTransform = newChild.getAttribute('transform') || '';
      const newTransform = existingTransform ? `${existingTransform} scale(${scale})` : `scale(${scale})`;

      newChild.setAttribute('transform', newTransform);
      newSvg.appendChild(newChild);
    });

    return newSvg;
  }

  /**
   * SVG 패스 데이터를 주어진 스케일로 변환합니다.
   * @static
   * @param {string} pathData - 원본 SVG 패스 데이터 문자열
   * @param {number} scale - 적용할 스케일 값 (예: 1.5는 50% 증가)
   * @returns {string} 스케일이 적용된 SVG 패스 데이터 문자열
   * @throws {Error} 유효하지 않은 입력값이나 처리 중 오류 발생 시
   */
  static scalePathData(pathData, scale) {
    // 입력값 검증
    if (typeof pathData !== 'string' || !pathData.trim()) {
      throw new Error('Invalid pathData: must be a non-empty string');
    }
    if (typeof scale !== 'number' || scale <= 0 || !Number.isFinite(scale)) {
      throw new Error('Invalid scale: must be a positive finite number');
    }

    // 명령어별 파라미터 개수 정의
    const paramCounts = {
      M: 2,
      m: 2, // moveto (x y)+
      L: 2,
      l: 2, // lineto (x y)+
      H: 1,
      h: 1, // horizontal lineto x+
      V: 1,
      v: 1, // vertical lineto y+
      C: 6,
      c: 6, // curveto (x1 y1 x2 y2 x y)+
      S: 4,
      s: 4, // smooth curveto (x2 y2 x y)+
      Q: 4,
      q: 4, // quadratic Bézier curveto (x1 y1 x y)+
      T: 2,
      t: 2, // smooth quadratic Bézier curveto (x y)+
      A: 7,
      a: 7, // elliptical arc (rx ry x-axis-rotation large-arc-flag sweep-flag x y)+
      Z: 0,
      z: 0, // closepath
    };

    try {
      // 숫자 추출 정규식 (음수, 지수 표기법, 소수점 포함)
      const numberRegex = /-?(?:\d*\.)?\d+(?:[eE][+-]?\d+)?/g;

      // 명령어 분리 정규식
      const commandRegex = /([MmLlHhVvCcSsQqTtAaZz])([^MmLlHhVvCcSsQqTtAaZz]*)/g;

      // 현재 위치 추적을 위한 변수
      let currentX = 0;
      let currentY = 0;

      return pathData.replace(commandRegex, (match, command, params) => {
        // 파라미터가 없거나 닫기 명령어인 경우
        if (!params.trim() || command === 'Z' || command === 'z') {
          return command;
        }

        try {
          // 현재 명령어의 파라미터 값들을 추출
          const numbers = params.match(numberRegex)?.map(Number);
          if (!numbers) {
            throw new Error(`Invalid parameters for command ${command}`);
          }

          const scaledNumbers = [];
          const isRelative = command === command.toLowerCase();
          const paramCount = paramCounts[command];

          // 파라미터 그룹 단위로 처리 (명령어가 연속으로 나올 수 있음)
          for (let i = 0; i < numbers.length; i += paramCount) {
            const params = numbers.slice(i, i + paramCount);
            if (params.length !== paramCount) {
              throw new Error(`Invalid parameter count for command ${command}`);
            }

            switch (command.toUpperCase()) {
              case 'M':
              case 'L': {
                const [x, y] = params;
                if (isRelative) {
                  currentX += x;
                  currentY += y;
                } else {
                  currentX = x;
                  currentY = y;
                }
                scaledNumbers.push(Number((x * scale).toFixed(4)), Number((y * scale).toFixed(4)));
                break;
              }
              case 'H': {
                const [x] = params;
                if (isRelative) {
                  currentX += x;
                } else {
                  currentX = x;
                }
                scaledNumbers.push(Number((x * scale).toFixed(4)));
                break;
              }
              case 'V': {
                const [y] = params;
                if (isRelative) {
                  currentY += y;
                } else {
                  currentY = y;
                }
                scaledNumbers.push(Number((y * scale).toFixed(4)));
                break;
              }
              case 'C': {
                const [x1, y1, x2, y2, x, y] = params;
                if (isRelative) {
                  currentX += x;
                  currentY += y;
                } else {
                  currentX = x;
                  currentY = y;
                }
                scaledNumbers.push(
                  Number((x1 * scale).toFixed(4)),
                  Number((y1 * scale).toFixed(4)),
                  Number((x2 * scale).toFixed(4)),
                  Number((y2 * scale).toFixed(4)),
                  Number((x * scale).toFixed(4)),
                  Number((y * scale).toFixed(4)),
                );
                break;
              }
              case 'S':
              case 'Q': {
                const [x1, y1, x, y] = params;
                if (isRelative) {
                  currentX += x;
                  currentY += y;
                } else {
                  currentX = x;
                  currentY = y;
                }
                scaledNumbers.push(
                  Number((x1 * scale).toFixed(4)),
                  Number((y1 * scale).toFixed(4)),
                  Number((x * scale).toFixed(4)),
                  Number((y * scale).toFixed(4)),
                );
                break;
              }
              case 'T': {
                const [x, y] = params;
                if (isRelative) {
                  currentX += x;
                  currentY += y;
                } else {
                  currentX = x;
                  currentY = y;
                }
                scaledNumbers.push(Number((x * scale).toFixed(4)), Number((y * scale).toFixed(4)));
                break;
              }
              case 'A': {
                const [rx, ry, xAxisRotation, largeArcFlag, sweepFlag, x, y] = params;
                if (isRelative) {
                  currentX += x;
                  currentY += y;
                } else {
                  currentX = x;
                  currentY = y;
                }
                // rx, ry는 항상 양수여야 함
                scaledNumbers.push(
                  Number(Math.abs(rx * scale).toFixed(4)),
                  Number(Math.abs(ry * scale).toFixed(4)),
                  Number(xAxisRotation), // 회전각은 스케일링하지 않음
                  Math.round(largeArcFlag), // 플래그는 0 또는 1
                  Math.round(sweepFlag), // 플래그는 0 또는 1
                  Number((x * scale).toFixed(4)),
                  Number((y * scale).toFixed(4)),
                );
                break;
              }
              default:
                throw new Error(`Unknown command: ${command}`);
            }
          }

          // 스케일된 값들을 문자열로 변환하여 반환
          return command + scaledNumbers.join(' ');
        } catch (error) {
          throw new Error(`Error processing command ${command}: ${error.message}`);
        }
      });
    } catch (error) {
      throw new Error(`Failed to scale path data: ${error.message}`);
    }
  }
}

/**
 * 이미지 처리 관련 유틸리티 기능을 제공하는 클래스
 * @class ImageUtils
 */
export class ImageUtils {
  /**
   * URL로부터 이미지 엘리먼트를 생성합니다.
   * @static
   * @async
   * @param {string} url - 이미지 URL (http://, https://, 또는 data:image/)
   * @returns {Promise<HTMLImageElement>} 생성된 이미지 엘리먼트
   * @throws {Error} 이미지 로드 실패 시 에러
   */
  static async createImageElFromUrl(url) {
    return loadImageFromUrl(UtilS.ensureUrlProtocol(url));
  }

  /**
   * Canvas 엘리먼트로부터 이미지 엘리먼트를 생성합니다.
   * @static
   * @async
   * @param {Object} params - 파라미터 객체
   * @param {HTMLCanvasElement} params.canvas - 원본 캔버스 엘리먼트
   * @param {string} [params.type='image/png'] - 이미지 타입
   * @param {number} [params.quality=1.0] - 이미지 품질 (0.0 ~ 1.0)
   * @param {string} [params.crossOrigin='anonymous'] - crossOrigin 설정 값
   * @returns {Promise<HTMLImageElement>} 생성된 이미지 엘리먼트
   * @throws {Error} 이미지 생성 실패 시 에러
   */
  static async createImageElFromCanvas({
    canvas,
    type = 'image/png',
    quality = 1.0,
    crossOrigin = 'anonymous',
  }) {
    return new Promise((resolve, reject) => {
      try {
        const img = new Image();
        img.crossOrigin = crossOrigin;

        img.onload = () => resolve(img);
        img.onerror = (error) => reject(new Error(`Failed to create image from canvas: ${error.message}`));

        img.src = canvas.toDataURL(type, quality);
      } catch (error) {
        reject(new Error(`Failed to convert canvas to image: ${error.message}`));
      }
    });
  }

  /**
   * 이미지나 캔버스에 패딩을 추가합니다.
   * @static
   * @async
   * @param {HTMLImageElement|HTMLCanvasElement} source - 원본 이미지 또는 캔버스 엘리먼트
   * @param {Object} [padding] - 패딩 설정
   * @param {number} [padding.top=0] - 상단 패딩
   * @param {number} [padding.right=0] - 우측 패딩
   * @param {number} [padding.bottom=0] - 하단 패딩
   * @param {number} [padding.left=0] - 좌측 패딩
   * @param {boolean} [isPixelated=true] - 픽셀화 스타일 적용 여부
   * @returns {Promise<HTMLCanvasElement>} 패딩이 적용된 Canvas 엘리먼트
   * @throws {Error} 패딩 적용 실패 시 에러
   */
  static addPaddingToSourceElement({
    source,
    padding = { top: 0, right: 0, bottom: 0, left: 0 },
    isPixelated = true,
  }) {
    return new Promise((resolve, reject) => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        const { top, bottom, right, left } = padding;

        canvas.width = source.width + left + right;
        canvas.height = source.height + top + bottom;

        // 픽셀화 스타일 설정
        ctx.imageSmoothingEnabled = !isPixelated;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(source, left, top, source.width, source.height);

        resolve(canvas);
      } catch (error) {
        reject(new Error(`Failed to add padding to source element: ${error.message}`));
      }
    });
  }

  /**
   * 픽셀 값을 밀리미터로 변환합니다.
   * @static
   * @param {number} px - 변환할 픽셀 값
   * @param {number} [dpi=300] - DPI (Dots Per Inch) 값
   * @returns {number} 밀리미터 값 (소수점 2자리까지)
   * @throws {Error} 유효하지 않은 입력값
   */
  static pxToMm(px, dpi = 300) {
    if (typeof px !== 'number' || px < 0) {
      throw new Error('Invalid pixel value: must be a non-negative number');
    }

    if (typeof dpi !== 'number' || dpi <= 0) {
      throw new Error('Invalid DPI value: must be a positive number');
    }

    // 1 inch = 25.4mm
    // px / dpi = inch
    // inch * 25.4 = mm
    const mm = (px / dpi) * 25.4;
    return Number(mm.toFixed(2));
  }

  /**
   * 밀리미터 값을 픽셀로 변환합니다.
   * @static
   * @param {number} mm - 변환할 밀리미터 값
   * @param {number} [dpi=300] - DPI (Dots Per Inch) 값
   * @returns {number} 픽셀 값 (정수)
   * @throws {Error} 유효하지 않은 입력값
   */
  static mmToPx(mm, dpi = 300) {
    if (typeof mm !== 'number' || mm < 0) {
      throw new Error('Invalid millimeter value: must be a non-negative number');
    }

    if (typeof dpi !== 'number' || dpi <= 0) {
      throw new Error('Invalid DPI value: must be a positive number');
    }

    // 1mm = 1/25.4 inches
    // inches * dpi = px
    const px = (mm / 25.4) * dpi;
    return Math.round(px);
  }
}

export class SizeUtils {
  /**
   * 사각형의 크기와 위치를 주어진 스케일 팩터로 조정합니다.
   * @param {Object} params - 스케일링 파라미터
   * @param {Object} params.rectSize - 원본 사각형 정보
   * @param {number} params.rectSize.x - x 좌표
   * @param {number} params.rectSize.y - y 좌표
   * @param {number} params.rectSize.width - 너비
   * @param {number} params.rectSize.height - 높이
   * @param {number} params.scaleFactor - 적용할 스케일 값
   * @returns {Object} 스케일링된 사각형 정보
   * @returns {number} returns.x - 스케일링된 x 좌표
   * @returns {number} returns.y - 스케일링된 y 좌표
   * @returns {number} returns.width - 스케일링된 너비
   * @returns {number} returns.height - 스케일링된 높이
   */
  static scaleRect({ rectSize, scaleFactor }) {
    return {
      x: rectSize.x * scaleFactor,
      y: rectSize.y * scaleFactor,
      width: rectSize.width * scaleFactor,
      height: rectSize.height * scaleFactor,
    };
  }

  /**
   * 커팅 라인 오프셋 패딩을 계산합니다.
   * @param {Object} params - 패딩 계산에 필요한 파라미터
   * @param {HTMLCanvasElement} params.canvas - 대상 캔버스
   * @param {number} params.dpi - dpi
   * @param {Object} params.cutlineMm - 커팅 라인 정보
   * @param {Object} params.paddingMm - 패딩 정보
   * @returns {Object} 각 방향향의 패딩 값
   */
  static calculateCutlineOffsetPaddingPx({ canvas, dpi, cutlineMm, paddingMm }) {
    const { OFFSET, SMOOTHNESS } = cutlineMm;

    const smoothnessPaddingPx = ((Math.max(canvas.width, canvas.height) * SMOOTHNESS) / 100) * 2;
    const offsetPaddingPx = ImageUtils.mmToPx(OFFSET, dpi);

    const basePaddingPx = smoothnessPaddingPx + offsetPaddingPx;

    const { TOP = 0, BOTTOM = 0, LEFT = 0, RIGHT = 0 } = paddingMm;

    return {
      top: basePaddingPx + ImageUtils.mmToPx(TOP, dpi),
      bottom: basePaddingPx + ImageUtils.mmToPx(BOTTOM, dpi),
      left: basePaddingPx + ImageUtils.mmToPx(LEFT, dpi),
      right: basePaddingPx + ImageUtils.mmToPx(RIGHT, dpi),
    };
  }

  /**
   * 원본 캔버스의 크기를 측정하고 최대 허용 크기를 초과하지 않는 최종 스케일을 계산합니다.
   * @param {Object} params - 스케일 계산에 필요한 파라미터
   * @param {HTMLCanvasElement} params.originalCanvas - 원본 캔버스
   * @param {number} params.targetScale - 적용할 스케일 값
   * @param {Object} params.maxSizeMm - 최대 허용 크기(mm) 정보
   * @param {number} params.maxSizeMm.WIDTH - 최대 허용 너비(mm)
   * @param {number} params.maxSizeMm.HEIGHT - 최대 허용 높이(mm)
   * @param {number} params.dpi - DPI 값
   * @returns {number} 제한된 최종 스케일 값
   */
  static calculateScaleWithSizeLimit({ originalCanvas, targetScale, maxSizeMm, dpi }) {
    const { width: originalDesignWidthPx, height: originalDesignHeightPx } =
      CanvasUtils.calculateImageBounds(originalCanvas);

    const scaledUpWidthPx = originalDesignWidthPx * targetScale;
    const scaledUpHeightPx = originalDesignHeightPx * targetScale;

    const { WIDTH, HEIGHT } = maxSizeMm;

    const maxWidthPx = ImageUtils.mmToPx(WIDTH, dpi);
    const maxHeightPx = ImageUtils.mmToPx(HEIGHT, dpi);

    const finalScale = this.#calculateLimitedScale({
      originalScale: targetScale,
      scaledUpWidthPx,
      scaledUpHeightPx,
      maxWidthPx,
      maxHeightPx,
    });

    return {
      finalScale,
      originalDesignWidthPx,
      originalDesignHeightPx,
    };
  }

  /**
   * 이미지가 최대 허용 크기를 초과하지 않도록 스케일을 제한합니다.
   * @private
   * @param {Object} params - 스케일 계산에 필요한 파라미터
   * @param {number} params.originalScale - 원본 스케일 값
   * @param {number} params.scaledUpWidthPx - 스케일이 적용된 너비 (픽셀)
   * @param {number} params.scaledUpHeightPx - 스케일이 적용된 높이 (픽셀)
   * @param {number} params.maxWidthPx - 최대 허용 너비 (픽셀)
   * @param {number} params.maxHeightPx - 최대 허용 높이 (픽셀)
   * @returns {number} 제한된 최종 스케일 값
   */
  static #calculateLimitedScale({
    originalScale,
    scaledUpWidthPx,
    scaledUpHeightPx,
    maxWidthPx,
    maxHeightPx,
  }) {
    const widthScaleFactor = maxWidthPx / scaledUpWidthPx;
    const heightScaleFactor = maxHeightPx / scaledUpHeightPx;
    const limitFactor = Math.min(widthScaleFactor, heightScaleFactor, 1);

    return originalScale * limitFactor;
  }

  /**
   * SVG와 캔버스를 경계에 맞게 조정합니다.
   * @param {Object} params - 피팅에 필요한 파라미터
   * @param {Object} params.cutlineStructure - 커트라인 구조 정보
   * @param {Object} params.cutlineStructure.cutlineSvgEl - 커트라인 SVG 엘리먼트
   * @param {Object} params.cutlineStructure.structurePoint - 구조물 기준점 좌표
   * @param {number} params.cutlineStructure.structurePoint.x - x 좌표
   * @param {number} params.cutlineStructure.structurePoint.y - y 좌표
   * @param {boolean} params.isPixelated - 픽셀화 스타일 적용 여부
   * @param {HTMLCanvasElement} params.printableReadyImageCanvas - 프린트 준비된 이미지 캔버스
   * @returns {Object} 피팅된 결과물
   * @returns {HTMLCanvasElement} returns.canvas - 경계에 맞게 잘린 캔버스
   * @returns {SVGElement} returns.cutlineSvgEl - 경계에 맞게 조정된 SVG 엘리먼트
   * @returns {Object} returns.structurePoint - 조정된 구조물 기준점 좌표
   * @returns {number} returns.structurePoint.x - 조정된 x 좌표
   * @returns {number} returns.structurePoint.y - 조정된 y 좌표
   */
  static makeResultFitted({ cutlineStructure, printableReadyImageCanvas, isPixelated }) {
    const { cutlineSvgEl, structurePoint } = cutlineStructure;

    const { svgElement: fittedSvgEl, bounds } = SvgUtils.fitSvgToPathBounds(cutlineSvgEl);
    const fittedCanvas = CanvasUtils.cropCanvas({
      canvas: printableReadyImageCanvas,
      bounds,
      isPixelated,
    });
    const fittedStructurePoint = {
      x: structurePoint.x - bounds.x,
      y: structurePoint.y - bounds.y,
    };

    return {
      canvas: fittedCanvas,
      cutlineSvgEl: fittedSvgEl,
      structurePoint: fittedStructurePoint,
    };
  }
}
